import { Button } from "../../components/ui/button";
import Common from "../../helper/common";
import {
    ResultStatus,
    RoleEnum,
} from "../../constants/appConstant";
import { AppCheckBox } from "../../components/appComponents";
import CardSnippet from "../../components/ui/card-snippet";
import { CardContent } from "../../components/ui/card";
import { AppSearchSelect } from "../../components/appComponents";
import { useEffect, useState } from "react";
import AddRolesByClub from "./addClubRoles";

const ClubPermissions = () => {

    const [clubList, setClubList] = useState([]);
    const [roleList, setRoleList] = useState([]);
    const [permissionsList, setPermissionsList] = useState([]);
    const [defaultPermissionsList, setDefaultPermissionsList] = useState([]);
    const [selectedClub, setSelectedClub] = useState(null);
    const [selectedRole, setSelectedRole] = useState(null);
    const [open, setOpen] = useState(false);
    const [selectedClubId, setSelectedClubId] = useState(null);
    const [selectedRoleId, setSelectedRoleId] = useState(null);
    const [checkedPermissions, setCheckedPermissions] = useState(new Set());
    const [saving, setSaving] = useState(false);
    const [defaultViewId, setDefaultViewId] = useState(null);
    const [roleDropdownKey, setRoleDropdownKey] = useState(0);

    useEffect(() => {
        getClubAccount();
        //getDefaultPermissions();
        //getPermissions();
    }, []);

    const getClubAccount = async () => {
        return new Promise(async (res) => {
            const response = await Common.ApiService.getInstance().request(
                "GetClubsDDL"
            );
            const data = response?.data ?? [];
            setClubList(data);

            setSelectedClub(null);
            setSelectedClubId(null);
            setRoleList([]);
            setSelectedRole(null);
            setSelectedRoleId(null);
            setPermissionsList([]);
            setDefaultPermissionsList([]);
        });
    };

    const getRolesByClub = async (clubId = null) => {
        try {
            const response = await Common.ApiService.getInstance().request(
                `GetRolesByClub${clubId ? `?clubId=${clubId}` : ""}`,
                "GET"
            );
            const data = response?.data ?? [];
            setRoleList(data);
            return data; // resolved value if caller wants it 
        } catch (err) {
            console.error("getRolesByClub error:", err);
            throw err;
        }
    };


    const getDefaultPermissions = async (clubId = null) => {

        let queryParams = [];

        if (clubId) queryParams.push(`clubId=${clubId}`);

        const queryString = queryParams.length > 0 ? `?${queryParams.join("&")}` : "";

        const response = await Common.ApiService.getInstance().request(
            `GetDefaultClubPermissions${queryString}`,
            "GET"
        );
        const _data = response?.data ?? [];
        setDefaultPermissionsList(_data);
        const defaultView = _data.find(p => p.isdefaultview === 1);
        setDefaultViewId(defaultView ? defaultView.id : null);
    };

    const getPermissionsByRoleAndClub = async (roleId = null, clubId = null) => {
        let queryParams = [];

        if (clubId) queryParams.push(`clubId=${clubId}`);
        if (roleId) queryParams.push(`roleId=${roleId}`);

        const queryString = queryParams.length > 0 ? `?${queryParams.join("&")}` : "";

        const response = await Common.ApiService.getInstance().request(
            `GetPermissionsForClubs${queryString}`,
            "GET"
        );
        const _data = response?.data ?? [];
        setPermissionsList(_data);

        // Identify the checked IDs
        const checkedIds = new Set(_data.map(p => p.id));
        setCheckedPermissions(checkedIds);

        const defaultView = _data.find(p => p.isdefaultview == 1);
        setDefaultViewId(defaultView ? defaultView.id : null);
    };

    const handleClose = () => {
        getRolesByClub(selectedClubId);
    };
    const handleClubChange = async (clubId) => {
        setSelectedClub(clubId);
        setSelectedClubId(clubId || null);

        // Reset role-related state
        setSelectedRole(null);
        setSelectedRoleId(null);
        setRoleList([]);

        // Force re-render of the role dropdown
        setRoleDropdownKey(prev => prev + 1);

        // Reset permissions state
        setPermissionsList([]);
        setDefaultPermissionsList([]);
        setCheckedPermissions(new Set());
        setDefaultViewId(null);
    };

    // Handle individual checkbox change
    const handlePermissionChange = (permissionId, isChecked) => {
        const newCheckedPermissions = new Set(checkedPermissions);

        if (isChecked) {
            newCheckedPermissions.add(permissionId);
        } else {
            newCheckedPermissions.delete(permissionId);

            // Clear defaultview and premiumallowed on unselect
            const updated = permissionsList.map(p => {
                if (p.id === permissionId) {
                    return {
                        ...p,
                        isdefaultview: 0,
                        premiumallowed: null,
                    };
                }
                return p;
            });
            setPermissionsList(updated);

            // If the unselected permission was the default view, clear defaultViewId
            if (defaultViewId === permissionId) {
                setDefaultViewId(null);
            }
        }

        setCheckedPermissions(newCheckedPermissions);
    };


    // Handle parent checkbox change (recursive)
    const handleParentPermissionChange = (node, isChecked) => {
        const newCheckedPermissions = new Set(checkedPermissions);

        // Function to recursively add/remove node and all children
        const updateNodeAndChildren = (currentNode, checked) => {
            if (checked) {
                newCheckedPermissions.add(currentNode.id);
            } else {
                newCheckedPermissions.delete(currentNode.id);
            }

            // Recursively handle children
            if (currentNode.children && currentNode.children.length > 0) {
                currentNode.children.forEach(child => {
                    updateNodeAndChildren(child, checked);
                });
            }
        };

        updateNodeAndChildren(node, isChecked);
        // If unchecking a parent, and the default view lies within this subtree, clear it
        if (!isChecked && defaultViewId) {
            const subtreeContainsId = (currentNode, id) => {
                if (!currentNode) return false;
                if (currentNode.id === id) return true;
                if (!currentNode.children || currentNode.children.length === 0) return false;
                return currentNode.children.some(child => subtreeContainsId(child, id));
            };

            if (subtreeContainsId(node, defaultViewId)) {
                const prevDefaultId = defaultViewId;
                setDefaultViewId(null);
                setPermissionsList(prev => prev.map(p => (
                    p.id === prevDefaultId ? { ...p, isdefaultview: 0 } : p
                )));
            }
        }
        setCheckedPermissions(newCheckedPermissions);
    };

    // Save permissions function
    const savePermissions = async () => {
        if (!selectedRole || !selectedClub) {
            Common.showToast("Please select a club and role first.", "Warning", "warning");
            return;
        }
        if (checkedPermissions.size === 0) {
            Common.showToast("Please select at least one permission.", "Warning", "warning");
            return;
        }
        if (!defaultViewId) {
            Common.showToast("Please select a Default View before saving.", "Warning", "warning");
            return;
        }
        setSaving(true);

        try {
            // Convert Set to Array
            const selectedPermissionIds = Array.from(checkedPermissions);

            // Prepare data for API call
            const payload = {
                clubid: selectedClubId,
                roleid: selectedRoleId,
                menuIds: selectedPermissionIds,
                defaultviewId: defaultViewId,
            };
            // Option 1: Using batch save (recommended)
            const response = await Common.ApiService.getInstance().request(
                "SaveClubPermissions",
                payload,
                "POST"
            );

            if (response.status === ResultStatus.Success || response.success) {
                Common.showToast("Permissions saved successfully!", "Success", "success");
                getPermissionsByRoleAndClub(selectedRoleId, selectedClub.id);
            } else {
                Common.showToast("Error saving permissions: " + (response.message || "Unknown error", "Error", "error"));
            }

        } catch (error) {
            console.error("Error saving permissions:", error);
            Common.showToast("Error saving permissions: " + error.message, "Error", "error");
        } finally {
            setSaving(false);
        }
    };

    const buildPermissionTree = (flatList) => {
        const map = {};
        const tree = [];

        flatList.forEach((item) => {
            map[item.id] = { ...item, children: [] };
        });

        flatList.forEach((item) => {
            if (item.parentId && map[item.parentId]) {
                map[item.parentId].children.push(map[item.id]);
            } else {
                tree.push(map[item.id]);
            }
        });

        const sortByOrder = (list) => {
            return list
                .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                .map((node) => ({
                    ...node,
                    children: sortByOrder(node.children),
                }));
        };

        return sortByOrder(tree);
    };

    //Helper function to count children recursively
    const countChildren = (node) => {
        if (!node.children || node.children.length === 0) {
            return 0;
        }

        let count = node.children.length;
        node.children.forEach(child => {
            count += countChildren(child);
        });

        return count;
    };

    // Check if all children are selected
    const areAllChildrenSelected = (node) => {
        if (!node.children || node.children.length === 0) {
            return checkedPermissions.has(node.id);
        }

        return node.children.every(child =>
            checkedPermissions.has(child.id) && areAllChildrenSelected(child)
        );
    };

    // Check if some children are selected (for indeterminate state)
    const areSomeChildrenSelected = (node) => {
        if (!node.children || node.children.length === 0) {
            return false;
        }

        return node.children.some(child =>
            checkedPermissions.has(child.id) || areSomeChildrenSelected(child)
        );
    };

    const renderPermissions = (nodes, level = 0) => {
        if (!nodes || nodes.length === 0 || nodes.every(n => !n?.name)) {
            return (
                <div className="text-gray-500 text-sm text-center">
                    No permissions found...
                </div>
            );
        }
        return nodes.map((node) => {
            const paddingStyle = {
                paddingLeft: `${level * 20}px`,
            };

            const childCount = countChildren(node);
            const premiumLabel = node.ispremium ? " (Premium Feature)" : "";
            const displayText =
                childCount > 0
                    ? `${node.name} ---------------- (${childCount})${premiumLabel}`
                    : `${node.name}${premiumLabel}`;

            const isChecked = checkedPermissions.has(node.id);
            const hasChildren = node.children && node.children.length > 0;
            const allChildrenSelected = hasChildren
                ? areAllChildrenSelected(node)
                : false;
            const someChildrenSelected = hasChildren
                ? areSomeChildrenSelected(node)
                : false;

            return (
                <div
                    key={node.id}
                    className="py-1 flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-dashed border-gray-200"
                >
                    {/* Left: Permission checkbox */}
                    <div className="flex items-center gap-2 w-full sm:w-3/4" style={paddingStyle}>
                        <AppCheckBox
                            text={displayText}
                            checked={hasChildren ? allChildrenSelected : isChecked}
                            indeterminate={hasChildren && someChildrenSelected && !allChildrenSelected}
                            onChange={(checked) => {
                                if (hasChildren) {
                                    handleParentPermissionChange(node, checked);
                                } else {
                                    handlePermissionChange(node.id, checked);
                                }
                            }}
                            className="border-default-300"
                        />
                    </div>

                    {/* Right: Default view toggle (radio-style) */}
                    {!hasChildren && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground pr-4 mt-2 sm:mt-0">
                            <span className="text-xs text-gray-500">Default View</span>
                            <input
                                type="radio"
                                name="defaultView"
                                checked={defaultViewId === node.id}
                                onChange={() => {
                                    // select the item if not selected
                                    if (!checkedPermissions.has(node.id)) {
                                        const newChecked = new Set(checkedPermissions);
                                        newChecked.add(node.id);
                                        setCheckedPermissions(newChecked);
                                    }

                                    const updated = permissionsList.map((p) => ({
                                        ...p,
                                        isdefaultview: p.id === node.id ? 1 : 0,
                                    }));
                                    setPermissionsList(updated);
                                    setDefaultViewId(node.id);
                                }}
                                className="form-radio h-4 w-4 text-primary"
                            />
                        </div>
                    )}

                    {/* Recursively render children */}
                    {hasChildren && (
                        <div className="w-full">{renderPermissions(node.children, level + 1)}</div>
                    )}
                </div>
            );
        });
    };

    const permissionTree = buildPermissionTree(defaultPermissionsList);

    // Select All logic
    const allPermissionIds = defaultPermissionsList.map(p => p.id);
    const isAllSelected = allPermissionIds.length > 0 && allPermissionIds.every(id => checkedPermissions.has(id));
    const isIndeterminate = checkedPermissions.size > 0 && !isAllSelected;

    const handleSelectAllChange = (checked) => {
        if (checked) {
            setCheckedPermissions(new Set(allPermissionIds));
        } else {
            setCheckedPermissions(new Set());
            // Clear default selection when unselecting all
            if (defaultViewId) {
                setDefaultViewId(null);
            }
            setPermissionsList(prev => prev.map(p => ({ ...p, isdefaultview: 0 })));
        }
    };

    return (
        <>
            <div className="md:grid md:grid-cols-6 gap-3 h-150 w-150 ">
                <AppSearchSelect
                    value={selectedClub}
                    onChange={async (val) => {
                        await handleClubChange(val);

                        if (val) {
                            debugger
                            await getRolesByClub(val);
                            await getDefaultPermissions(val);

                            // Focus role dropdown
                            document.getElementById("roleDropdown")?.focus();
                        }
                    }}
                    placeholder="Select Club"
                    label={"Clubs"}
                    data={clubList}
                    className={"mb-3 col-span-1"}
                />
                <AppSearchSelect
                    key={roleDropdownKey}
                    id="roleDropdown"
                    value={selectedRole}
                    onChange={async (val) => {
                        setSelectedRole(val);
                        setSelectedRoleId(val || null);

                        if (val && selectedClubId) {
                            await getPermissionsByRoleAndClub(val, selectedClubId);
                        }
                    }}
                    placeholder="Select Role"
                    label="Roles"
                    data={roleList}
                    className="mb-3 col-span-1"
                />

                <Button className="lg:mt-6 h-8 mb-5" onClick={() => {
                    setOpen(true);
                }}>
                    Add Role
                </Button>
            </div>
            <div className="flex flex-col gap-2">
                <CardSnippet
                    title="Club Permissions"
                    addButton={() => (
                        <Button className="h-8" onClick={savePermissions}
                            disabled={saving || !selectedRole || !selectedClub}>
                            Save Permissions
                        </Button>
                    )}
                >
                    <CardContent className="p-4">
                        {permissionTree && permissionTree.length > 0 && permissionTree.some(n => n?.name) && (
                            <>
                                <div className="mb-4">
                                    <AppCheckBox
                                        text={<span className="font-bold text-md">Select All Permissions</span>}
                                        checked={isAllSelected}
                                        indeterminate={isIndeterminate}
                                        onChange={handleSelectAllChange}
                                        className="border-default-300 w-6 h-6"
                                    />
                                </div>
                                <hr className="border-gray-300 mb-4" />
                            </>
                        )}
                        {renderPermissions(permissionTree)}
                    </CardContent>

                </CardSnippet>
            </div>

            {/* Initialize the dialog */}
            <AddRolesByClub
                open={open}
                setOpen={setOpen}
                onClose={handleClose}
                clubId={selectedClubId}
            />
        </>
    );
};


export default ClubPermissions;